import LanguageSelector from '@uturn/portal/components/language-selector';
import { Button, Logo } from '@uturn/ui-kit';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { LoginIssuesNotice } from '../components/login-issues-notice';
import { PasswordResetRequestForm } from '../components/password-reset-request-form';
import { SignInForm } from '../components/sign-in-form';

export function AuthSignIn() {
	const [passwordReset, setPasswordReset] = useState(false);

	const { t } = useTranslation();

	const toOldPlatform = async (url: string) => {
		window.location.href = `${window.location.origin}${url}`
			.replace('staging', 'test')
			.replace('uturn-now', 'uturn-system');
	};

	return (
		<>
			<Helmet
				title={
					passwordReset
						? t('pages.auth.password_reset.title', 'Reset password').toString()
						: t('pages.auth.sign-in.title', 'Sign in').toString()
				}
			/>
			<div className="container relative min-h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
				<div className="absolute bottom-12 right-12 sm:right-4 sm:top-4 md:right-8 md:top-8">
					<div className="flex items-center gap-4">
						<Button
							variant="ghost"
							onClick={async () => toOldPlatform('/register')}
						>
							{t('auth.register', 'Register')}
						</Button>
						<LanguageSelector />
					</div>
				</div>
				<div className="bg-primary text-primary-foreground hidden h-screen flex-col items-center justify-center p-12 lg:flex">
					<div className="flex w-fit flex-col items-stretch gap-4">
						{/* TODO: seasonal logo pending rebrand — this page used to render the
						    seasonal <Logo> (useSeasonalLogo) during christmas/easter. Restore
						    the seasonal variant here once rebranded seasonal art is available. */}
						<Logo className="h-auto w-full fill-primary-foreground dark:fill-primary-foreground" />
						<span className="whitespace-nowrap text-center text-3xl font-semibold">
							{t(
								'pages.auth.sign-in.tagline',
								'Every container. A promise kept.',
							)}
						</span>
					</div>
				</div>
				<div className="w-full lg:p-8">
					<div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
						<Logo className="mx-auto h-10 w-auto lg:hidden" />
						{passwordReset ? (
							<>
								<div className="flex flex-col space-y-2 text-center">
									<h1 className="text-2xl font-semibold tracking-tight">
										{t(
											'pages.auth.password_reset_request.title',
											'Request a password reset',
										)}
									</h1>
									<p className="text-muted-foreground text-sm">
										{t(
											'pages.auth.password_reset_request.description',
											'Enter your email and we ll email you a link to reset your password.',
										)}
									</p>
								</div>
								<PasswordResetRequestForm />
								<div className="text-muted-foreground text-center text-sm">
									<Button
										variant="link"
										onClick={() => setPasswordReset(false)}
									>
										{t(
											'pages.auth.password_reset_request.actions.back',
											'Back to login',
										)}
									</Button>
								</div>
							</>
						) : (
							<>
								<div className="flex flex-col space-y-2 text-center">
									<h1 className="text-2xl font-semibold tracking-tight">
										{t(
											'pages.auth.sign-in.heading',
											'Lets sign in to your account',
										)}
									</h1>
									<p className="text-muted-foreground text-sm">
										{t(
											'pages.auth.sign-in.description',
											'Enter your email and password and we ll get you on your way.',
										)}
									</p>
								</div>
								<SignInForm />
								<p className="text-muted-foreground text-center text-sm">
									{t('pages.auth.sign-in.question', 'Are you a carrier?')}{' '}
									<Button
										variant="link"
										onClick={() => toOldPlatform('/login')}
									>
										{t(
											'pages.auth.sign-in.actions.carrier-login',
											'Please sign in here',
										)}
									</Button>
								</p>
								<p className="text-muted-foreground text-center text-sm">
									<Button variant="link" onClick={() => setPasswordReset(true)}>
										{t(
											'pages.auth.sign-in.actions.reset-password',
											'Forgot password?',
										)}
									</Button>
								</p>
							</>
						)}
					</div>
					{!passwordReset && (
						<div className="mx-auto mt-8 w-full max-w-2xl">
							<LoginIssuesNotice />
						</div>
					)}
				</div>
			</div>
		</>
	);
}
